import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';

import { GetBusinessCityDocument } from '@xing-com/crate-jobs-graphql';

export type LocationSuggestion = {
  cityId: string;
  name: string;
  meta: string;
};

type UseSearchLocationArgs = {
  initialLocation?: string | null;
  initialCityId?: string | null;
  skipBusinessCityQuery?: boolean;
  onChange?: () => void;
};

type UseSearchLocationResult = {
  location: string;
  cityId: string;
  onChangeLocation: (location: string) => void;
  onSelectLocation: (suggestion: LocationSuggestion) => void;
};

export const useSearchLocation = ({
  initialLocation,
  initialCityId,
  skipBusinessCityQuery,
  onChange,
}: UseSearchLocationArgs): UseSearchLocationResult => {
  const locationValue = initialLocation ?? '';
  const cityIdValue = initialCityId ?? '';

  const [location, setLocation] = useState(locationValue);
  const [cityId, setCityId] = useState(cityIdValue);

  // Check if the URL params have been changed (i.e. by resetting the filters or navigating back with the browser)
  useEffect(() => {
    if (locationValue !== location) {
      setLocation(locationValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationValue]);

  useEffect(() => {
    if (cityIdValue !== cityId) {
      setCityId(cityIdValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityIdValue]);

  useQuery(GetBusinessCityDocument, {
    skip: skipBusinessCityQuery,
    onCompleted: ({ viewer }) => {
      const businessCity = viewer?.xingId?.location?.city;
      if (businessCity) {
        setLocation(businessCity);
      }
    },
  });

  const handleOnChangeLocation = (name: string): void => {
    onChange?.();
    setLocation(name);
    setCityId('');
  };

  const handleOnSelectLocation = ({
    name,
    cityId,
  }: LocationSuggestion): void => {
    onChange?.();
    setLocation(name);
    setCityId(cityId);
  };

  return {
    location,
    cityId,
    onChangeLocation: handleOnChangeLocation,
    onSelectLocation: handleOnSelectLocation,
  };
};
